/**
 * Client for the admin POS-matching inspector endpoints (feat-115).
 *
 * Contracts:
 *  - Spec:  worktrees/feat-115-admin-pos-matching-inspector/spec.md
 *  - ADR 0001 (read-model + endpoint contract) — D1 (two typed endpoints),
 *    D2 (admin-only gate, fail-closed IDOR), D5 (vendor-neutral grain),
 *    D6 (match semantics), D7 (payload shapes).
 *
 * Two admin-only endpoints (no /admin/execute-select — the SQL stays server-side):
 *  - List:   GET /api/admin/locations/{loc}/pos/orders/inspect-list?date_from&date_to[&source][&limit]
 *  - Detail: GET /api/admin/locations/{loc}/pos/orders/{source}/{vendor_order_id}/inspect
 * The `/api` prefix is the router mount (main.py include_router prefix="/api");
 * the route-decorator paths in ADR §D1/§D7 omit it.
 *
 * The backend coerces DECIMAL/JSON columns to numbers/objects at the response
 * edge (ADR §D7) — these types are clean numbers/objects and the client does
 * NOT re-parseFloat.
 *
 * Fake mode: when `config.environment === 'fake'` (npm run start:fake / Jest),
 * every call returns canned data so the page renders without a backend. A
 * `?pos_inspect_state=<state>` URL param flips the detail between branches so QA
 * can exercise the no-covering-shift clean empty state (AC5).
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';

// ---------- Domain types (mirror ADR §D7 verbatim) ----------

export type PosSource = 'square' | 'revel' | string;

/** Order header — ADR §D7 `order`. */
export interface PosOrderHeader {
  source: PosSource;
  vendor_order_id: string;
  state: string; // e.g. 'closed', 'open', 'canceled'
  opened_at_utc: string; // ISO-8601 UTC
  opened_at_local: string; // location-local, no offset suffix
  timezone: string; // IANA, e.g. 'America/Los_Angeles'
  location_id: string;
  location_name: string;
  final_total: number;
  net_sales: number;
  currency: string;
  snapshot_version: number;
  received_at: string;
}

/** ADR §D7 `snapshots_summary`. */
export interface SnapshotsSummary {
  total_snapshots: number;
  current_version: number;
  is_current: boolean;
  /** true if a non-current snapshot was superseded. */
  superseded: boolean;
}

/** ADR §D7 `covering_shifts[]`. */
export interface CoveringShift {
  shift_id: string;
  server_id: string;
  server_name: string;
  shift_start: string;
  shift_end: string;
  shift_offset_seconds: number;
  matched_at: string;
}

/**
 * ADR §D7 `timeline.entries[].service_criteria[]` — a criterion APPLICABLE to
 * this interaction (the list is a superset of fulfilled-only, so the UI can show
 * attempted-but-not-fulfilled — the diagnostic point). Both flags are
 * backend-computed; the FE renders them straight (no client re-derivation).
 */
export interface ServiceCriterion {
  criterion_key: string;
  quality_level: number | null;
  /** whether the criterion was met in this interaction (vs merely applicable). */
  fulfilled: boolean;
  /** membership in the active prompt_template_version service-criteria keys. */
  on_scorecard: boolean;
}

/**
 * Fields on every timeline entry. Per ADR §D4.1 (multi-shift is the NORM): the
 * window routinely spans 2+ overlapping covering shifts, so entries are a single
 * chronological stream sorted by `time_vs_order_seconds` (a common axis — each
 * entry's value is computed against ITS shift's offset). Each entry carries
 * explicit `server_id`/`server_name` (resolved shift_id→shift.server), so the UI
 * labels each line by name without re-joining. Attribution is mic/shift-based
 * (crosstalk caveat) — best-effort, surfaced as-is.
 */
interface TimelineEntryBase {
  id: string;
  shift_id: string;
  server_id: string;
  /** null when the shift's server is unmapped — UI falls back to server_id/"Unknown". */
  server_name: string | null;
  start_time: number; // seconds-from-shift-start
  time_vs_order_seconds: number; // start_time − that shift's shift_offset_seconds
  in_matcher_window: boolean;
}

export interface TranscriptTimelineEntry extends TimelineEntryBase {
  kind: 'transcript_part';
  /** server-vs-customer turn WITHIN a stream; orthogonal to which named server (use server_name for that). */
  speaker: number;
  text: string;
}

export interface InteractionTimelineEntry extends TimelineEntryBase {
  kind: 'interaction';
  interaction_type: string; // server_upsell, cashier_upsell, drink_promotion, missed_upsell, garnish_complete, greeting, …
  quality_level: number | null;
  is_upsell_attempt: boolean;
  service_criteria: ServiceCriterion[];
  /** text of the transcript part(s) that produced this interaction. */
  transcript_text: string;
}

export type TimelineEntry = TranscriptTimelineEntry | InteractionTimelineEntry;

export interface MatcherWindow {
  pre_seconds: number;
  post_seconds: number;
  start_local: string;
  end_local: string;
}

export interface ContextWindow {
  pre_seconds: number;
  post_seconds: number;
}

export interface Timeline {
  matcher_window: MatcherWindow;
  context_window: ContextWindow;
  entries: TimelineEntry[];
}

/** ADR §D7 `line_items[].classification` (feat-114 LLM classification). */
export interface LineItemClassification {
  categories: string[];
  is_featured_drink: boolean;
  prompt_template_version_id: string;
  classified_at: string;
}

export interface PosLineItem {
  id: string;
  position: number;
  name: string;
  quantity: number;
  unit_price: number;
  total_money: number;
  /** null when the item has not been classified yet. */
  classification: LineItemClassification | null;
}

/** ADR §D7 `associations[]` — attempts gathered (pos_order_interaction_link). */
export interface Association {
  shift_interaction_id: string;
  interaction_type: string;
  shift_id: string;
  time_diff_seconds: number;
  matched_at: string;
}

/**
 * ADR §D7 `conversions[].converted_via`. The feat-114 matcher stores this keyed by
 * CATEGORY → matched item names, e.g. {"beverage": ["Diet Coke"]}; a single conversion
 * may match across multiple categories. The backend (pos_inspector.assemble_detail)
 * emits the stored object verbatim, so the FE must render this shape — NOT a flat
 * {bucket, item_names}. (feat-117: the original {bucket, item_names} type crashed on
 * real data because the fake fixture used the wrong shape; see feedback_qa_verify_real_endpoints.)
 */
export type ConvertedVia = Record<string, string[]>;

/** ADR §D7 `conversions[]` — criterion-grain (pos_interaction_conversion), incl converted=false. */
export interface Conversion {
  shift_interaction_id: string;
  criterion_key: string;
  target_categories: string[];
  converted: boolean;
  converted_via: ConvertedVia | null;
  quality_level: number | null;
  time_diff_seconds: number;
  matched_at: string;
  /** backend-computed reason for converted=false (e.g. "attempt = sandwich anchor; order had no sandwich"). */
  non_conversion_reason: string | null;
}

/** ADR §D5/§D7 — Revel item-grain legacy links; populated only when conversions == []. */
export interface LegacyItemLink {
  pos_order_line_item_id: string; // backend sends a string id (UUID); unrendered
  item_name: string;
  quantity: number;
  unit_price: number;
  shift_interaction_id: string;
  match_rule: string; // drink_promotion, metric_1, metric_2, metric_3
  time_diff_seconds: number;
  /** backend field is `explanation` (normalized) — NOT match_explanation (old Revel groupOrderRows shape). */
  explanation: string | null;
}

/** Full detail payload — ADR §D7. */
export interface PosOrderInspectDetail {
  order: PosOrderHeader;
  snapshots_summary: SnapshotsSummary;
  covering_shifts: CoveringShift[];
  /** string when covering_shifts == [] (AC5 clean-zero case), else null. */
  no_covering_shift_reason: string | null;
  timeline: Timeline;
  line_items: PosLineItem[];
  associations: Association[];
  conversions: Conversion[];
  /** Revel item-grain; populated only when conversions == [] (ADR §D5). */
  legacy_item_links: LegacyItemLink[];
}

/** A row in the recent-orders picker — ADR §D7 list shape. */
export interface PosInspectListRow {
  source: PosSource;
  vendor_order_id: string;
  state: string;
  opened_at_utc: string;
  opened_at_local: string;
  final_total: number;
  covering_shift_count: number;
  association_count: number;
  conversion_count: number;
  is_multi_snapshot: boolean;
}

export interface PosInspectListResponse {
  location_id: string;
  location_name: string;
  window: { date_from: string; date_to: string };
  /** true if the result hit the hard LIMIT (ADR §D3) — UI must surface "results truncated". */
  capped: boolean;
  orders: PosInspectListRow[];
}

export interface PosInspectListParams {
  locationId: string;
  dateFrom: string; // ISO
  dateTo: string; // ISO
  source?: PosSource;
  limit?: number; // default 200, hard cap 500 (backend-enforced)
}

/** A location that has POS orders (feat-120) — backs the inspector's searchable location dropdown. */
export interface PosInspectorLocation {
  location_id: string;
  location_name: string;
  tenant_name: string;
}

export interface PosInspectorLocationsResponse {
  locations: PosInspectorLocation[];
}

// ---------- HTTP plumbing (mirrors squareConnectionService.ts) ----------

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

async function api<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const resp = await authedApiFetch(backendUrl(path), { method: 'GET', headers });
  if (resp.status === 404) {
    // ADR §D2: cross-tenant / missing order fails closed as 404 (not 403).
    throw new PosInspectorNotFoundError(path);
  }
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`POS inspector API ${path} ${resp.status}: ${text}`);
  }
  return resp.json();
}

export class PosInspectorNotFoundError extends Error {
  constructor(path: string) {
    super(`POS inspector: order not found or not accessible (${path})`);
    this.name = 'PosInspectorNotFoundError';
  }
}

// ---------- Public API ----------

const BASE = '/api/admin/locations';

/** Recent-orders picker (ADR §D1 list). Location-keyed — caller selects a location first. */
export async function listOrdersForInspection(
  params: PosInspectListParams,
): Promise<PosInspectListResponse> {
  if (config.environment === 'fake') {
    return fakeListResponse(params);
  }
  const q = new URLSearchParams({ date_from: params.dateFrom, date_to: params.dateTo });
  if (params.source) q.set('source', params.source);
  if (params.limit != null) q.set('limit', String(params.limit));
  const path = `${BASE}/${encodeURIComponent(params.locationId)}/pos/orders/inspect-list?${q.toString()}`;
  return api<PosInspectListResponse>(path);
}

/** Full matching chain for one order (ADR §D1 detail). */
export async function inspectOrder(
  locationId: string,
  source: PosSource,
  vendorOrderId: string,
): Promise<PosOrderInspectDetail> {
  if (config.environment === 'fake') {
    return fakeDetailResponse(source, vendorOrderId);
  }
  const path =
    `${BASE}/${encodeURIComponent(locationId)}/pos/orders/` +
    `${encodeURIComponent(source)}/${encodeURIComponent(vendorOrderId)}/inspect`;
  return api<PosOrderInspectDetail>(path);
}

/**
 * Locations that have POS orders (feat-120) — the only locations the inspector can inspect,
 * for the searchable location dropdown. Derived from the data on the backend; no flag/config.
 */
export async function listInspectorLocations(): Promise<PosInspectorLocation[]> {
  if (config.environment === 'fake') {
    return [
      { location_id: 'loc-llp', location_name: 'L&L Hawaiian BBQ — Pearlridge', tenant_name: 'Ladle & Leaf' },
      { location_id: 'loc-llk', location_name: 'L&L Hawaiian BBQ — Kapolei', tenant_name: 'Ladle & Leaf' },
      { location_id: 'loc-grove', location_name: 'The Grove — Downtown', tenant_name: 'Grove Café' },
    ];
  }
  const resp = await api<PosInspectorLocationsResponse>('/api/admin/pos/inspector/locations');
  return resp.locations;
}

// ---------- Fake mode ----------
// Renders both the happy path and the AC5 no-covering-shift empty state without
// a backend. `?pos_inspect_state=no_shift` flips the detail to the clean-zero case.

function fakeStateOverride(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('pos_inspect_state');
  } catch {
    return null;
  }
}

const FAKE_LOCATION_NAME = 'L&L Hawaiian BBQ — Pearlridge';

function fakeListResponse(params: PosInspectListParams): PosInspectListResponse {
  return {
    location_id: params.locationId,
    location_name: FAKE_LOCATION_NAME,
    window: { date_from: params.dateFrom, date_to: params.dateTo },
    capped: false,
    orders: [
      {
        source: 'square',
        vendor_order_id: 'sq-ORDER-001',
        state: 'closed',
        opened_at_utc: '2026-06-20T18:03:11Z',
        opened_at_local: '2026-06-20T11:03:11',
        final_total: 23.5,
        covering_shift_count: 1,
        association_count: 2,
        conversion_count: 1,
        is_multi_snapshot: true,
      },
      {
        source: 'square',
        vendor_order_id: 'sq-ORDER-002',
        state: 'closed',
        opened_at_utc: '2026-06-20T19:41:55Z',
        opened_at_local: '2026-06-20T12:41:55',
        final_total: 8.75,
        covering_shift_count: 0,
        association_count: 0,
        conversion_count: 0,
        is_multi_snapshot: false,
      },
      // Revel source row → click-through exercises the legacy item-grain detail (ADR §D5).
      {
        source: 'revel',
        vendor_order_id: 'rv-ORDER-003',
        state: 'closed',
        opened_at_utc: '2026-06-20T20:15:00Z',
        opened_at_local: '2026-06-20T13:15:00',
        final_total: 31.25,
        covering_shift_count: 1,
        association_count: 1,
        conversion_count: 0,
        is_multi_snapshot: false,
      },
    ],
  };
}

function fakeDetailResponse(source: PosSource, vendorOrderId: string): PosOrderInspectDetail {
  // Revel order → the legacy item-grain branch (conversions == [] && legacy_item_links
  // populated, ADR §D5). Exercises the path the Square fixtures never hit.
  if (source === 'revel') return fakeRevelDetail(vendorOrderId);
  const noShift = fakeStateOverride() === 'no_shift' || vendorOrderId === 'sq-ORDER-002';
  const order: PosOrderHeader = {
    source,
    vendor_order_id: vendorOrderId,
    state: 'closed',
    opened_at_utc: noShift ? '2026-06-20T19:41:55Z' : '2026-06-20T18:03:11Z',
    opened_at_local: noShift ? '2026-06-20T12:41:55' : '2026-06-20T11:03:11',
    timezone: 'Pacific/Honolulu',
    location_id: 'fake-loc-1',
    location_name: FAKE_LOCATION_NAME,
    final_total: noShift ? 8.75 : 23.5,
    net_sales: noShift ? 8.1 : 21.1,
    currency: 'USD',
    snapshot_version: noShift ? 1 : 5,
    received_at: '2026-06-20T19:50:00Z',
  };

  if (noShift) {
    return {
      order,
      snapshots_summary: { total_snapshots: 1, current_version: 1, is_current: true, superseded: false },
      covering_shifts: [],
      no_covering_shift_reason:
        'No recorded shift covered this order’s time — the order cannot be placed in any shift-relative timeline. This is the correct clean-zero case (no covering shift → no associations or conversions).',
      timeline: {
        matcher_window: { pre_seconds: 180, post_seconds: 30, start_local: '2026-06-20T12:38:55', end_local: '2026-06-20T12:42:25' },
        context_window: { pre_seconds: 300, post_seconds: 90 },
        entries: [],
      },
      line_items: [
        {
          id: 'li-x1',
          position: 0,
          name: 'Dubai Chocolate Bar',
          quantity: 1,
          unit_price: 8.75,
          total_money: 8.75,
          classification: {
            categories: ['dessert', 'featured'],
            is_featured_drink: false,
            prompt_template_version_id: 'ptv-9',
            classified_at: '2026-06-20T19:50:30Z',
          },
        },
      ],
      associations: [],
      conversions: [],
      legacy_item_links: [],
    };
  }

  return {
    order,
    snapshots_summary: { total_snapshots: 3, current_version: 5, is_current: true, superseded: false },
    covering_shifts: [
      {
        shift_id: 'shift-77',
        server_id: 'srv-12',
        server_name: 'Jane K.',
        shift_start: '2026-06-20T17:00:00Z',
        shift_end: '2026-06-20T22:00:00Z',
        shift_offset_seconds: 842,
        matched_at: '2026-06-20T19:50:05Z',
      },
      // D4.1 — multi-shift is the norm: a second overlapping covering shift.
      {
        shift_id: 'shift-88',
        server_id: 'srv-20',
        server_name: 'Bill T.',
        shift_start: '2026-06-20T16:30:00Z',
        shift_end: '2026-06-20T21:30:00Z',
        shift_offset_seconds: 2642,
        matched_at: '2026-06-20T19:50:05Z',
      },
    ],
    no_covering_shift_reason: null,
    timeline: {
      // Single chronological stream merged across both covering shifts, sorted by
      // time_vs_order_seconds. Each entry carries its own server_id/server_name (D4.1).
      matcher_window: { pre_seconds: 180, post_seconds: 30, start_local: '2026-06-20T11:00:11', end_local: '2026-06-20T11:03:41' },
      context_window: { pre_seconds: 300, post_seconds: 90 },
      entries: [
        {
          kind: 'transcript_part',
          id: 'tp-650',
          shift_id: 'shift-77',
          server_id: 'srv-12',
          server_name: 'Jane K.',
          start_time: 592,
          time_vs_order_seconds: -250,
          speaker: 1,
          text: 'Just looking, thanks.',
          in_matcher_window: false,
        },
        {
          kind: 'transcript_part',
          id: 'tp-700',
          shift_id: 'shift-77',
          server_id: 'srv-12',
          server_name: 'Jane K.',
          start_time: 700.5,
          time_vs_order_seconds: -141.5,
          speaker: 0,
          text: 'Hi, welcome in! Can I get you started with one of our soup combos today?',
          in_matcher_window: true,
        },
        {
          kind: 'interaction',
          id: 'int-9001',
          shift_id: 'shift-77',
          server_id: 'srv-12',
          server_name: 'Jane K.',
          start_time: 712,
          time_vs_order_seconds: -130,
          interaction_type: 'server_upsell',
          quality_level: 3,
          is_upsell_attempt: true,
          in_matcher_window: true,
          service_criteria: [
            { criterion_key: 'soup_anchor_sandwich_upsell', quality_level: 3, fulfilled: true, on_scorecard: true },
          ],
          transcript_text: 'Can I get you started with one of our soup combos today? They pair great with a sandwich.',
        },
        {
          kind: 'transcript_part',
          id: 'tp-bill-1',
          shift_id: 'shift-88',
          server_id: 'srv-20',
          server_name: 'Bill T.',
          start_time: 2542,
          time_vs_order_seconds: -100,
          speaker: 0,
          text: 'I can ring you up over here whenever you’re ready.',
          in_matcher_window: true,
        },
        {
          kind: 'interaction',
          id: 'int-9002',
          shift_id: 'shift-77',
          server_id: 'srv-12',
          server_name: 'Jane K.',
          start_time: 760,
          time_vs_order_seconds: -82,
          interaction_type: 'drink_promotion',
          quality_level: 2,
          is_upsell_attempt: true,
          in_matcher_window: true,
          service_criteria: [
            { criterion_key: 'featured_drink_promo', quality_level: 2, fulfilled: true, on_scorecard: false },
          ],
          transcript_text: 'We also have our featured mango lemonade if you’d like to add a drink.',
        },
      ],
    },
    line_items: [
      {
        id: 'li-1',
        position: 0,
        name: 'Soup Combo',
        quantity: 1,
        unit_price: 9.5,
        total_money: 9.5,
        classification: {
          categories: ['soup'],
          is_featured_drink: false,
          prompt_template_version_id: 'ptv-9',
          classified_at: '2026-06-20T18:05:00Z',
        },
      },
      {
        id: 'li-2',
        position: 1,
        name: 'Mango Lemonade (Featured)',
        quantity: 1,
        unit_price: 4.0,
        total_money: 4.0,
        classification: {
          categories: ['drink', 'featured'],
          is_featured_drink: true,
          prompt_template_version_id: 'ptv-9',
          classified_at: '2026-06-20T18:05:00Z',
        },
      },
    ],
    associations: [
      { shift_interaction_id: 'int-9001', interaction_type: 'server_upsell', shift_id: 'shift-77', time_diff_seconds: -130, matched_at: '2026-06-20T19:50:05Z' },
      { shift_interaction_id: 'int-9002', interaction_type: 'drink_promotion', shift_id: 'shift-77', time_diff_seconds: -82, matched_at: '2026-06-20T19:50:05Z' },
    ],
    conversions: [
      {
        shift_interaction_id: 'int-9002',
        criterion_key: 'featured_drink_promo',
        target_categories: ['featured', 'drink'],
        converted: true,
        converted_via: { featured: ['Mango Lemonade (Featured)'], beverage: ['Diet Coke'] },
        quality_level: 2,
        time_diff_seconds: -82,
        matched_at: '2026-06-20T19:50:05Z',
        non_conversion_reason: null,
      },
      {
        shift_interaction_id: 'int-9001',
        criterion_key: 'soup_anchor_sandwich_upsell',
        target_categories: ['sandwich'],
        converted: false,
        converted_via: null,
        quality_level: 3,
        time_diff_seconds: -130,
        matched_at: '2026-06-20T19:50:05Z',
        non_conversion_reason: 'attempt = sandwich anchor; order had no sandwich',
      },
    ],
    legacy_item_links: [],
  };
}

function fakeRevelDetail(vendorOrderId: string): PosOrderInspectDetail {
  return {
    order: {
      source: 'revel',
      vendor_order_id: vendorOrderId,
      state: 'closed',
      opened_at_utc: '2026-06-20T20:15:00Z',
      opened_at_local: '2026-06-20T13:15:00',
      timezone: 'Pacific/Honolulu',
      location_id: 'fake-loc-1',
      location_name: FAKE_LOCATION_NAME,
      final_total: 31.25,
      net_sales: 28.4,
      currency: 'USD',
      snapshot_version: 1,
      received_at: '2026-06-20T20:20:00Z',
    },
    snapshots_summary: { total_snapshots: 1, current_version: 1, is_current: true, superseded: false },
    covering_shifts: [
      {
        shift_id: 'shift-91',
        server_id: 'srv-31',
        server_name: 'Lesli M.',
        shift_start: '2026-06-20T18:00:00Z',
        shift_end: '2026-06-20T23:00:00Z',
        shift_offset_seconds: 1500,
        matched_at: '2026-06-20T20:20:05Z',
      },
    ],
    no_covering_shift_reason: null,
    timeline: {
      matcher_window: { pre_seconds: 180, post_seconds: 30, start_local: '2026-06-20T13:12:00', end_local: '2026-06-20T13:15:30' },
      context_window: { pre_seconds: 300, post_seconds: 90 },
      entries: [
        {
          kind: 'interaction',
          id: 'int-rv-1',
          shift_id: 'shift-91',
          server_id: 'srv-31',
          server_name: 'Lesli M.',
          start_time: 1400,
          time_vs_order_seconds: -100,
          interaction_type: 'server_upsell',
          quality_level: 2,
          is_upsell_attempt: true,
          in_matcher_window: true,
          service_criteria: [{ criterion_key: 'metric_1', quality_level: 2, fulfilled: true, on_scorecard: true }],
          transcript_text: 'Would you like to make it a Steak Shawarma plate?',
        },
      ],
    },
    line_items: [
      {
        id: 'rli-1',
        position: 0,
        name: 'Steak Shawarma Plate',
        quantity: 1,
        unit_price: 14.25,
        total_money: 14.25,
        classification: null, // Revel items may be unclassified (item-grain path predates feat-114 cache)
      },
    ],
    associations: [
      { shift_interaction_id: 'int-rv-1', interaction_type: 'server_upsell', shift_id: 'shift-91', time_diff_seconds: -100, matched_at: '2026-06-20T20:20:05Z' },
    ],
    // Revel item-grain path: no criterion-grain conversions, legacy links carry the attribution.
    conversions: [],
    legacy_item_links: [
      {
        pos_order_line_item_id: 'rli-90211',
        item_name: 'Steak Shawarma Plate',
        quantity: 1,
        unit_price: 14.25,
        shift_interaction_id: 'int-rv-1',
        match_rule: 'metric_1',
        time_diff_seconds: -100,
        explanation: 'Server offered the Steak Shawarma plate 100s before this item was rung up.',
      },
    ],
  };
}
